import { useCallback, useEffect, useRef, useState } from 'react';
import { getGoogleClientId, getGoogleUserInfoUrl, GOOGLE_GSI_CLIENT_SCRIPT_URL } from '../config';

let scriptLoadPromise = null;

const loadGoogleScript = () => {
  if (window.google?.accounts?.oauth2) {return Promise.resolve();}
  if (scriptLoadPromise) {return scriptLoadPromise;}
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GOOGLE_GSI_CLIENT_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later mount try again instead of caching the failure for the whole page.
      scriptLoadPromise = null;
      script.remove();
      reject(new Error('Failed to load Google Identity Services script'));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
};

// Wraps Google Identity Services' OAuth2 token client (GOOGLE_GSI_CLIENT_SCRIPT_URL).
// An earlier version of this hook rendered Google's drop-in button (accounts.id.renderButton)
// off-screen and forwarded a synthetic click to it from our own styled button - but that
// rendered button is a cross-origin <iframe>, and a JS .click() on the wrapper div in our page
// can never reach Google's handler inside it, so nothing happened. initTokenClient +
// requestAccessToken() is Google's supported path for a custom-styled trigger: calling
// requestAccessToken() directly inside our own button's real onClick is a genuine, trusted user
// gesture, so Google opens its actual account-chooser popup for it.
const useGoogleSignIn = ({ onSuccess, onError } = {}) => {
  const clientId = getGoogleClientId();
  const [isReady, setIsReady] = useState(false);
  const tokenClientRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!clientId) {return undefined;}
    let cancelled = false;

    // A load failure is not reported here - the customer hasn't asked for Google yet. It
    // surfaces as the inline error if they click the button while it isn't ready.
    loadGoogleScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.oauth2) {return;}
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid profile email',
          callback: (tokenResponse) => {
            if (tokenResponse.error) {
              // access_denied is the customer declining Google's consent screen - a cancel, like
              // closing the popup, not a failure.
              if (tokenResponse.error !== 'access_denied') {onErrorRef.current?.(new Error(tokenResponse.error));}
              return;
            }
            // The access token is only used here, transiently, to ask Google for the profile
            // fields we need - it's never logged or rendered anywhere.
            fetch(getGoogleUserInfoUrl(), {
              headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
            })
              .then((res) => {
                if (!res.ok) {throw new Error('Failed to fetch Google profile');}
                return res.json();
              })
              .then((profile) => {
                onSuccessRef.current?.({
                  firstName: profile.given_name || '',
                  lastName: profile.family_name || '',
                  email: profile.email || '',
                });
              })
              .catch((error) => onErrorRef.current?.(error));
          },
          error_callback: (error) => {
            // Fires when the user closes the popup before choosing an account, or Google can't
            // open it at all (e.g. blocked popup, unauthorized origin). Closing the popup is an
            // expected cancellation, not a failure worth surfacing - the form just stays as-is.
            if (error?.type === 'popup_closed') {return;}
            onErrorRef.current?.(error);
          },
        });
        if (!cancelled) {setIsReady(true);}
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // requestAccessToken() stays synchronous inside the click, so Google's popup isn't blocked.
  const triggerSignIn = useCallback(() => {
    if (!clientId) {return;}
    if (!tokenClientRef.current) {
      onErrorRef.current?.(new Error('Google sign-in is not ready'));
      return;
    }
    tokenClientRef.current.requestAccessToken();
  }, [clientId]);

  return {
    isConfigured: Boolean(clientId),
    isReady,
    triggerSignIn,
  };
};

export default useGoogleSignIn;
